// =============================================================================
// Admin E-Sign Envelopes Management Page
// =============================================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  EnvelopeIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  EnvelopeOpenIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Card } from '../../../components/ui';
import esignService from '../../../services/esign.service';
import type { SignatureEnvelope, EnvelopeStatus } from '../../../types/esign';
import { ENVELOPE_STATUS_LABELS, ENVELOPE_STATUS_COLORS, SIGNER_STATUS_LABELS } from '../../../types/esign';

// =============================================================================
// Constants
// =============================================================================

const PAGE_SIZE = 20;

const STATUS_BADGE_COLORS: Record<string, string> = {
  gray: 'bg-gray-100 text-gray-700',
  blue: 'bg-blue-50 text-blue-700',
  yellow: 'bg-yellow-50 text-yellow-700',
  green: 'bg-green-50 text-green-700',
  red: 'bg-red-50 text-red-700',
  orange: 'bg-orange-50 text-orange-700',
};

const STATUS_FILTERS: { label: string; value: EnvelopeStatus | '' }[] = [
  { label: 'All', value: '' },
  { label: 'Draft', value: 'ENVELOPE_DRAFT' },
  { label: 'Sent', value: 'ENVELOPE_SENT' },
  { label: 'Partially Signed', value: 'ENVELOPE_PARTIALLY_SIGNED' },
  { label: 'Completed', value: 'ENVELOPE_COMPLETED' },
  { label: 'Declined', value: 'ENVELOPE_DECLINED' },
  { label: 'Voided', value: 'ENVELOPE_VOIDED' },
  { label: 'Expired', value: 'ENVELOPE_EXPIRED' },
];

// =============================================================================
// Component
// =============================================================================

export default function EnvelopesPage() {
  const navigate = useNavigate();
  const [envelopes, setEnvelopes] = useState<SignatureEnvelope[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

  // Debounce search input
  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to page 1 on new search
    }, 300);
    return () => clearTimeout(debounceTimer.current);
  }, [search]);

  // Load envelopes when filters change
  useEffect(() => {
    loadEnvelopes();
  }, [statusFilter, debouncedSearch, page]);

  const loadEnvelopes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await esignService.listEnvelopes({
        status: statusFilter || undefined,
        search: debouncedSearch || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setEnvelopes(result.data);
      setTotalCount(result.meta?.pagination?.total ?? result.data.length);
    } catch (err) {
      console.error('Failed to load envelopes:', err);
      setError(err instanceof Error ? err.message : 'Failed to load envelopes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch, page]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Loading skeleton
  if (loading && envelopes.length === 0) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-12 bg-gray-200 rounded-lg" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded-xl" />
          ))}
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">E-Sign Envelopes</h1>
          <p className="mt-1 text-sm text-gray-500">Track and manage signature requests</p>
        </div>
        <button
          onClick={() => navigate('/admin/esign/envelopes/new')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800"
        >
          <PlusIcon className="h-4 w-4" />
          New Envelope
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <Card padding="md">
          <div className="flex items-center justify-between text-red-700">
            <span className="text-sm">Error: {error}</span>
            <button onClick={() => { setError(null); loadEnvelopes(); }} className="text-sm font-medium underline hover:no-underline">
              Retry
            </button>
          </div>
        </Card>
      )}

      {/* Search & Filters */}
      <Card padding="md">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search envelopes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="w-full md:w-52">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card padding="sm">
          <p className="text-sm text-gray-500">Total Results</p>
          <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">On This Page</p>
          <p className="text-2xl font-bold text-gray-400">{envelopes.length}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Page</p>
          <p className="text-2xl font-bold text-blue-600">{page} of {totalPages || 1}</p>
        </Card>
        <Card padding="sm">
          <p className="text-sm text-gray-500">Showing</p>
          <p className="text-2xl font-bold text-green-600">
            {envelopes.length > 0 ? `${(page - 1) * PAGE_SIZE + 1}-${(page - 1) * PAGE_SIZE + envelopes.length}` : '0'}
          </p>
        </Card>
      </div>

      {/* Envelopes List */}
      <div className={`space-y-3 ${loading ? 'opacity-50 pointer-events-none' : ''}`}>
        {envelopes.map((env) => {
          const statusColor = ENVELOPE_STATUS_COLORS[env.status] || 'gray';
          const signedCount = env.signers?.filter((s) => s.status === 'SIGNER_SIGNED').length || 0;
          const totalSigners = env.signers?.length || 0;

          return (
            <Card key={env.id} padding="md" hover>
              <Link
                to={`/admin/esign/envelopes/${env.id}`}
                className="flex flex-col lg:flex-row lg:items-center justify-between gap-4"
              >
                {/* Envelope Info */}
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="hidden sm:flex w-12 h-12 rounded-xl bg-gray-100 items-center justify-center flex-shrink-0">
                    {env.status === 'ENVELOPE_COMPLETED' ? (
                      <CheckCircleIcon className="h-6 w-6 text-green-500" />
                    ) : env.status === 'ENVELOPE_DRAFT' ? (
                      <EnvelopeIcon className="h-6 w-6 text-gray-400" />
                    ) : env.status === 'ENVELOPE_DECLINED' || env.status === 'ENVELOPE_VOIDED' ? (
                      <XCircleIcon className="h-6 w-6 text-red-400" />
                    ) : env.status === 'ENVELOPE_EXPIRED' ? (
                      <ClockIcon className="h-6 w-6 text-orange-400" />
                    ) : (
                      <EnvelopeOpenIcon className="h-6 w-6 text-blue-500" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-semibold text-gray-900 truncate">{env.subject}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_BADGE_COLORS[statusColor] || STATUS_BADGE_COLORS.gray}`}>
                        {ENVELOPE_STATUS_LABELS[env.status] || env.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5 truncate">{env.documentName}</p>
                    <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <UserGroupIcon className="h-3.5 w-3.5" />
                        {signedCount}/{totalSigners} signed
                      </span>
                      {env.sentAt && <span>Sent {formatDate(env.sentAt)}</span>}
                      {env.expiresAt && <span>Expires {formatDate(env.expiresAt)}</span>}
                      {!env.sentAt && <span>Created {formatDate(env.createdAt)}</span>}
                    </div>
                  </div>
                </div>

                {/* Signers preview */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="hidden md:flex flex-col items-end gap-0.5">
                    {env.signers?.slice(0, 3).map((s) => (
                      <div key={s.id} className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 truncate max-w-[140px]">{s.name}</span>
                        <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${
                          s.status === 'SIGNER_SIGNED' ? 'bg-green-50 text-green-700' :
                          s.status === 'SIGNER_DECLINED' ? 'bg-red-50 text-red-700' :
                          s.status === 'SIGNER_VIEWED' ? 'bg-yellow-50 text-yellow-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {SIGNER_STATUS_LABELS[s.status]?.replace('Signer ', '') || s.status.replace('SIGNER_', '')}
                        </span>
                      </div>
                    ))}
                    {(env.signers?.length || 0) > 3 && (
                      <span className="text-[10px] text-gray-400">+{(env.signers?.length || 0) - 3} more</span>
                    )}
                  </div>
                </div>
              </Link>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {envelopes.length === 0 && !error && !loading && (
        <Card padding="lg">
          <div className="text-center py-12">
            <EnvelopeIcon className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Envelopes Found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {search || statusFilter
                ? 'Try adjusting your search or filters'
                : 'Create your first envelope to send a document for signature'}
            </p>
            {!search && !statusFilter && (
              <button
                onClick={() => navigate('/admin/esign/envelopes/new')}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800"
              >
                <PlusIcon className="h-4 w-4" />
                New Envelope
              </button>
            )}
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 7) {
                pageNum = i + 1;
              } else if (page <= 4) {
                pageNum = i + 1;
              } else if (page >= totalPages - 3) {
                pageNum = totalPages - 6 + i;
              } else {
                pageNum = page - 3 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                    page === pageNum
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
